import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2, Plus, CheckCircle, XCircle, Zap, Clock, DollarSign,
  Target, Megaphone, Play, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const OBJECTIVES = [
  { value: 'lead_gen', label: 'Lead Generation' },
  { value: 'brand_awareness', label: 'Brand Awareness' },
  { value: 'event_promo', label: 'Promozione Evento' },
  { value: 'retargeting', label: 'Retargeting' },
  { value: 'conversion', label: 'Conversione' },
];

const PLATFORMS = [
  { value: 'meta', label: 'Meta (FB+IG)' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'google', label: 'Google Ads' },
];

function StatusBadge({ proposal }) {
  if (proposal.execution_confirmed_at) return <Badge className="bg-emerald-100 text-emerald-700">Eseguita</Badge>;
  if (proposal.execution_failed) return <Badge className="bg-red-100 text-red-700">Esecuzione Fallita</Badge>;
  if (proposal.decision === 'approved') return <Badge className="bg-blue-100 text-blue-700">Approvata</Badge>;
  if (proposal.decision === 'rejected') return <Badge className="bg-red-100 text-red-700">Rifiutata</Badge>;
  if (proposal.awaiting_approval) return <Badge className="bg-amber-100 text-amber-700">In Attesa</Badge>;
  return <Badge className="bg-slate-100 text-slate-700">Sconosciuto</Badge>;
}

function ProposalCard({ proposal, onDecide, onExecute }) {
  const [note, setNote] = useState('');
  const [executing, setExecuting] = useState(false);

  const handleExecute = async () => {
    setExecuting(true);
    try { await onExecute(proposal.id); } finally { setExecuting(false); }
  };

  const canExecute = proposal.decision === 'approved' && !proposal.execution_confirmed_at && !proposal.execution_failed;

  return (
    <Card className="border-[#E2E8F0] hover:shadow-md transition-shadow" data-testid={`proposal-${proposal.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#1A3A5C] flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-[#2E75B6]" />
              {proposal.objective ? OBJECTIVES.find(o => o.value === proposal.objective)?.label || proposal.objective : 'Campagna'}
            </h3>
            {proposal.product_name && (
              <p className="text-xs text-[#64748B] mt-0.5">Prodotto: {proposal.product_name}</p>
            )}
          </div>
          <StatusBadge proposal={proposal} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5 text-[#64748B]" />
            <span className="text-xs text-[#475569]">{proposal.budget_daily_chf} CHF/gg</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-[#64748B]" />
            <span className="text-xs text-[#475569]">{proposal.duration_days} giorni</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5 text-[#64748B]" />
            <span className="text-xs text-[#475569]">{(proposal.platforms || []).join(', ')}</span>
          </div>
        </div>

        {proposal.notes && <p className="text-xs text-[#64748B] italic">{proposal.notes}</p>}

        {proposal.external_confirmation_id && (
          <div className="text-xs text-emerald-600 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            ID: {proposal.external_confirmation_id}
          </div>
        )}
        {proposal.execution_error && (
          <div className="text-xs text-red-600 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {proposal.execution_error}
          </div>
        )}
        {proposal.decision_note && (
          <p className="text-xs text-[#94A3B8]">Nota: {proposal.decision_note}</p>
        )}

        {proposal.awaiting_approval && (
          <div className="space-y-2 pt-1 border-t border-[#E2E8F0]">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota decisione (opzionale)..." className="h-7 text-xs" data-testid={`note-${proposal.id}`} />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => onDecide(proposal.id, 'approved', note)} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${proposal.id}`}>
                <CheckCircle className="w-3 h-3 mr-1" />Approva
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDecide(proposal.id, 'rejected', note)} className="h-7 text-xs text-red-600 border-red-200" data-testid={`reject-${proposal.id}`}>
                <XCircle className="w-3 h-3 mr-1" />Rifiuta
              </Button>
            </div>
          </div>
        )}

        {canExecute && (
          <div className="pt-1 border-t border-[#E2E8F0]">
            <Button size="sm" onClick={handleExecute} disabled={executing} className="h-7 text-xs bg-[#2E75B6] hover:bg-[#2E75B6]/90 text-white" data-testid={`execute-${proposal.id}`}>
              {executing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Esegui Campagna
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdsMonitorPage({ embedded }) {
  const [proposals, setProposals] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    objective: 'lead_gen', product_id: '', budget_daily_chf: 20,
    duration_days: 7, platforms: ['meta'], notes: ''
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [propRes, prodRes] = await Promise.all([
        api.get('/ads-monitor/proposals'),
        api.get('/offering/products').catch(() => ({ data: [] }))
      ]);
      setProposals(propRes.data);
      setProducts(prodRes.data);
    } catch {} finally { setLoading(false); }
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await api.post('/ads-monitor/propose-campaign', form);
      toast.success('Proposta campagna creata');
      setCreateOpen(false);
      setForm({ objective: 'lead_gen', product_id: '', budget_daily_chf: 20, duration_days: 7, platforms: ['meta'], notes: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore creazione'); } finally { setSubmitting(false); }
  };

  const handleDecide = async (proposalId, decision, note) => {
    try {
      await api.patch(`/ads-monitor/proposals/${proposalId}/decide`, { decision, note });
      toast.success(decision === 'approved' ? 'Proposta approvata' : 'Proposta rifiutata');
      fetchAll();
    } catch { toast.error('Errore decisione'); }
  };

  const handleExecute = async (proposalId) => {
    try {
      const res = await api.post(`/ads-monitor/execute/${proposalId}`);
      if (res.data.success) {
        toast.success(`Campagna eseguita — ID: ${res.data.external_id}`);
      } else {
        toast.error(`Esecuzione fallita: ${res.data.error}`);
      }
      fetchAll();
    } catch { toast.error('Errore esecuzione'); }
  };

  const togglePlatform = (p) => {
    setForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter(x => x !== p) : [...prev.platforms, p]
    }));
  };

  const filtered = tab === 'awaiting' ? proposals.filter(p => p.awaiting_approval)
    : tab === 'approved' ? proposals.filter(p => p.decision === 'approved')
    : tab === 'executed' ? proposals.filter(p => p.execution_confirmed_at)
    : proposals;

  const awaitingCount = proposals.filter(p => p.awaiting_approval).length;
  const approvedCount = proposals.filter(p => p.decision === 'approved' && !p.execution_confirmed_at && !p.execution_failed).length;
  const executedCount = proposals.filter(p => p.execution_confirmed_at).length;
  const totalBudget = proposals.filter(p => p.decision === 'approved').reduce((s, p) => s + (p.budget_daily_chf || 0) * (p.duration_days || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1A3A5C]" /></div>;

  return (
    <div className={embedded ? '' : 'animate-fade-in'} data-testid="ads-monitor-page">
      {!embedded && (
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Campagne ADV</h1>
          <p className="page-subtitle">Gestione proposte e campagne pubblicitarie</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" data-testid="new-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />Nuova Campagna
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-[#1A3A5C]">Proponi Campagna</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Obiettivo *</Label>
                <Select value={form.objective} onValueChange={(v) => setForm({ ...form, objective: v })}>
                  <SelectTrigger data-testid="campaign-objective"><SelectValue /></SelectTrigger>
                  <SelectContent>{OBJECTIVES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {products.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Prodotto (opzionale)</Label>
                  <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="campaign-product"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno</SelectItem>
                      {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Budget giornaliero (CHF)</Label>
                  <Input type="number" value={form.budget_daily_chf} onChange={(e) => setForm({ ...form, budget_daily_chf: parseFloat(e.target.value) || 0 })} data-testid="campaign-budget" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Durata (giorni)</Label>
                  <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 1 })} data-testid="campaign-duration" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Piattaforme</Label>
                <div className="flex gap-2">
                  {PLATFORMS.map(p => (
                    <button
                      key={p.value}
                      onClick={() => togglePlatform(p.value)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${form.platforms.includes(p.value) ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#94A3B8]'}`}
                      data-testid={`platform-${p.value}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Note</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Note aggiuntive..." data-testid="campaign-notes" />
              </div>
              <Button onClick={handleCreate} disabled={submitting} className="w-full bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" data-testid="submit-campaign-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Megaphone className="w-4 h-4 mr-2" />}
                Proponi Campagna
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      )}

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-500" />
            <div><p className="text-xs text-[#64748B]">In Attesa</p><p className="text-xl font-bold text-[#0F172A]" data-testid="awaiting-count">{awaitingCount}</p></div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-blue-500" />
            <div><p className="text-xs text-[#64748B]">Approvate</p><p className="text-xl font-bold text-[#0F172A]" data-testid="approved-count">{approvedCount}</p></div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4 flex items-center gap-3">
            <Zap className="w-5 h-5 text-emerald-500" />
            <div><p className="text-xs text-[#64748B]">Eseguite</p><p className="text-xl font-bold text-[#0F172A]" data-testid="executed-count">{executedCount}</p></div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-[#1A3A5C]" />
            <div><p className="text-xs text-[#64748B]">Budget Totale</p><p className="text-xl font-bold text-[#0F172A]" data-testid="total-budget">{totalBudget.toLocaleString()} CHF</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="ads-tabs">
          <TabsTrigger value="all" data-testid="tab-all">Tutte ({proposals.length})</TabsTrigger>
          <TabsTrigger value="awaiting" data-testid="tab-awaiting">In Attesa ({awaitingCount})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approvate ({approvedCount})</TabsTrigger>
          <TabsTrigger value="executed" data-testid="tab-executed">Eseguite ({executedCount})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Megaphone className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
              <p className="text-[#64748B]">Nessuna proposta in questa categoria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map(p => (
                <ProposalCard key={p.id} proposal={p} onDecide={handleDecide} onExecute={handleExecute} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
